/**
 * Adds line numbers to code blocks.
 * Creates a gutter element alongside the code content.
 */

import { BaseEnricher } from './BaseEnricher.js';
import type { EnricherConfig, EnricherContext } from './types.js';

export class LineNumberEnricher extends BaseEnricher {
  constructor(config: EnricherConfig = {}) {
    super({ priority: 98, ...config });
  }

  async process(container: HTMLElement, _context: EnricherContext): Promise<void> {
    container.querySelectorAll('pre').forEach(pre => {
      const code = pre.querySelector('code');
      if (!code) return;

      // Skip if already has line numbers
      if (pre.querySelector('.line-numbers')) return;

      this.addLineNumbers(pre as HTMLElement, code as HTMLElement);
    });
  }

  /**
   * Add line number gutter to a pre element.
   */
  private addLineNumbers(pre: HTMLElement, code: HTMLElement): void {
    const lineCount = this.countLines(code);
    const gutter = this.createGutter(lineCount);
    pre.insertBefore(gutter, code);
  }

  /**
   * Count lines in a code element.
   */
  private countLines(code: HTMLElement): number {
    const text = code.textContent ?? '';
    // Remove trailing newline that marked.js adds
    const trimmed = text.endsWith('\n') ? text.slice(0, -1) : text;
    return trimmed.split('\n').length;
  }

  /**
   * Create the line number gutter element.
   */
  private createGutter(lineCount: number): HTMLSpanElement {
    const gutter = document.createElement('span');
    gutter.className = 'line-numbers';
    gutter.setAttribute('aria-hidden', 'true');

    const lines: string[] = [];
    for (let i = 1; i <= lineCount; i++) {
      lines.push(String(i));
    }
    gutter.textContent = lines.join('\n');

    return gutter;
  }

  /**
   * Refresh line numbers for a specific pre element.
   * Called by other enrichers after content changes.
   */
  refreshLineNumbers(pre: HTMLElement): void {
    const code = pre.querySelector('code');
    if (!code) return;

    const existing = pre.querySelector('.line-numbers');
    if (existing) {
      const lineCount = this.countLines(code as HTMLElement);
      const lines: string[] = [];
      for (let i = 1; i <= lineCount; i++) {
        lines.push(String(i));
      }
      existing.textContent = lines.join('\n');
    }
  }
}

// Export a singleton instance for refreshing from other enrichers
export const lineNumberer = new LineNumberEnricher();
